// Report review routing — Users & Roles → Report Review tab. Admin-managed
// "who reviews what": director/administrator pick named reviewer(s) (+ cc) per
// template category or specific template code, instead of any director/administrator
// being able to approve/reject (closes the same-person-wrote-and-approved-it loophole).
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, ShieldCheck, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

export default function ReviewRoutingPanel() {
    const [routes, setRoutes] = useState([]);
    const [options, setOptions] = useState({ categories: [], templates: [], users: [] });
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { mode: create|edit, r? }

    function load() {
        setLoading(true);
        Promise.all([api.get("/review-routes"), api.get("/review-routes/options")])
            .then(([r1, r2]) => { setRoutes(r1.data); setOptions(r2.data); })
            .catch(() => toast.error("Could not load review routing"))
            .finally(() => setLoading(false));
    }
    useEffect(() => { load(); }, []);

    const userById = Object.fromEntries(options.users.map((u) => [u.id, u]));

    async function remove(r) {
        if (!window.confirm(`Remove the review route for "${r.label}"? Approve/reject for this report type will fall back to any Director/Administrator.`)) return;
        try { await api.delete(`/review-routes/${r.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    return (
        <div data-testid="review-routing-panel" className="space-y-4">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <p className="text-[13px] max-w-lg" style={{ color: "#6F6A64" }}>
                    Which named reviewer(s) must approve/reject each report type after it's Submitted — instead of any Director/Administrator. The person who submitted a report can never approve their own, regardless of routing.
                </p>
                <button data-testid="review-route-new" onClick={() => setDialog({ mode: "create" })}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white shrink-0" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <Plus size={15} /> New review route
                </button>
            </div>

            {loading ? (
                <div className="py-16 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : routes.length === 0 ? (
                <div className="py-16 text-center text-[#9A968F] border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>
                    No review routes yet. Reports with no route here fall back to any Director/Administrator (except the submitter).
                </div>
            ) : (
                <div className="bg-white border rounded-[12px] shadow-brand-sm divide-y" style={{ borderColor: "#E5E0DA" }}>
                    {routes.map((r) => (
                        <div key={r.id} data-testid={`review-route-row-${r.id}`} className="flex items-center gap-3 px-4 py-3">
                            <ShieldCheck size={15} className="text-[#9F4212] shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-[#1F1F1F] text-[13.5px] flex items-center gap-2">
                                    {r.label}
                                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: "#F5F3F0", color: "#6F6A64" }}>
                                        {r.match_type === "category" ? "category" : "template"}
                                    </span>
                                    {!r.active && <span className="text-[10px] font-bold uppercase tracking-wider text-[#B33A2E]">inactive</span>}
                                </div>
                                <div className="text-[11.5px] mt-0.5 flex items-center gap-1.5 flex-wrap" style={{ color: "#6F6A64" }}>
                                    <UserCheck size={12} />
                                    Reviewer{r.reviewer_user_ids.length > 1 ? "s" : ""}: {r.reviewer_user_ids.map((id) => userById[id]?.name || "Unknown user").join(", ") || "—"}
                                    {r.cc_user_ids.length > 0 && <span> · CC: {r.cc_user_ids.map((id) => userById[id]?.name || "Unknown user").join(", ")}</span>}
                                </div>
                            </div>
                            <button data-testid={`review-route-edit-${r.id}`} onClick={() => setDialog({ mode: "edit", r })}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}>
                                <Pencil size={12} /> Edit
                            </button>
                            <button data-testid={`review-route-delete-${r.id}`} onClick={() => remove(r)}
                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5]">
                                <Trash2 size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {dialog && (
                <RouteDialog mode={dialog.mode} initial={dialog.r} options={options}
                    onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />
            )}
        </div>
    );
}

function RouteDialog({ mode, initial, options, onClose, onSaved }) {
    const [matchType, setMatchType] = useState(initial?.match_type || "template_code");
    const [matchValue, setMatchValue] = useState(initial?.match_value || "");
    const [label, setLabel] = useState(initial?.label || "");
    const [reviewerIds, setReviewerIds] = useState(initial?.reviewer_user_ids || []);
    const [ccIds, setCcIds] = useState(initial?.cc_user_ids || []);
    const [active, setActive] = useState(initial?.active !== false);
    const [busy, setBusy] = useState(false);

    const matchOptions = matchType === "category"
        ? options.categories.map((c) => ({ value: c, text: c }))
        : options.templates.map((t) => ({ value: t.code, text: t.name }));

    function onPickMatchValue(v) {
        setMatchValue(v);
        if (!label.trim() || label === matchValue) {
            const found = matchOptions.find((o) => o.value === v);
            if (found) setLabel(found.text);
        }
    }

    function toggle(list, setList, id) {
        setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
    }

    async function save() {
        if (!matchValue) { toast.error("Choose a report type"); return; }
        if (!label.trim()) { toast.error("Label is required"); return; }
        if (mode === "create" && reviewerIds.length === 0) { toast.error("Pick at least one reviewer"); return; }
        setBusy(true);
        try {
            if (mode === "create") {
                await api.post("/review-routes", { match_type: matchType, match_value: matchValue, label: label.trim(), reviewer_user_ids: reviewerIds, cc_user_ids: ccIds });
            } else {
                await api.patch(`/review-routes/${initial.id}`, { label: label.trim(), reviewer_user_ids: reviewerIds, cc_user_ids: ccIds, active });
            }
            toast.success(mode === "create" ? "Review route created" : "Review route saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="review-route-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-lg border max-h-[85vh] flex flex-col" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between shrink-0" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">{mode === "create" ? "New review route" : `Edit — ${initial?.label}`}</h3>
                    <button data-testid="review-route-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4 overflow-y-auto flex-1">
                    {mode === "create" && (
                        <>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Match by
                                <select data-testid="review-route-match-type" value={matchType} onChange={(e) => { setMatchType(e.target.value); setMatchValue(""); }} className={inputCls + " mt-1 font-normal normal-case"}>
                                    <option value="template_code">Specific report template</option>
                                    <option value="category">Whole category (all templates in it)</option>
                                </select>
                            </label>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">
                                {matchType === "category" ? "Category" : "Report template"}
                                <select data-testid="review-route-match-value" value={matchValue} onChange={(e) => onPickMatchValue(e.target.value)} className={inputCls + " mt-1 font-normal normal-case"}>
                                    <option value="">— choose —</option>
                                    {matchOptions.map((o) => <option key={o.value} value={o.value}>{o.text}</option>)}
                                </select>
                            </label>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Display label
                                <input data-testid="review-route-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. CDM" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                        </>
                    )}
                    {mode === "edit" && (
                        <>
                            <div className="text-[12.5px] rounded-lg px-3 py-2" style={{ background: "#F5F3F0", color: "#6F6A64" }}>
                                {initial.match_type === "category" ? "Category" : "Template"}: <b>{initial.match_value}</b> (can't be changed — delete and recreate to re-match)
                            </div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Display label
                                <input data-testid="review-route-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. CDM" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                        </>
                    )}
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block mb-1.5">Reviewer(s) — can approve/reject/reopen</label>
                        <div className="border rounded-lg divide-y max-h-40 overflow-y-auto" style={{ borderColor: "#E5E0DA" }}>
                            {options.users.map((u) => (
                                <label key={u.id} data-testid={`review-route-reviewer-${u.id}`} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-[#F6F4F0]">
                                    <input type="checkbox" checked={reviewerIds.includes(u.id)} onChange={() => toggle(reviewerIds, setReviewerIds, u.id)} />
                                    <span className="flex-1">{u.name} <span className="text-[11px]" style={{ color: "#9A968F" }}>· {u.email}</span></span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block mb-1.5">CC (email only — informed, not an approver)</label>
                        <div className="border rounded-lg divide-y max-h-32 overflow-y-auto" style={{ borderColor: "#E5E0DA" }}>
                            {options.users.map((u) => (
                                <label key={u.id} data-testid={`review-route-cc-${u.id}`} className="flex items-center gap-2 px-3 py-2 text-[13px] cursor-pointer hover:bg-[#F6F4F0]">
                                    <input type="checkbox" checked={ccIds.includes(u.id)} onChange={() => toggle(ccIds, setCcIds, u.id)} />
                                    <span className="flex-1">{u.name} <span className="text-[11px]" style={{ color: "#9A968F" }}>· {u.email}</span></span>
                                </label>
                            ))}
                        </div>
                    </div>
                    {mode === "edit" && (
                        <label className="flex items-center gap-2 text-[13px] cursor-pointer">
                            <input data-testid="review-route-active" type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
                            Active
                        </label>
                    )}
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[#F5F3F0] shrink-0" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="review-route-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {mode === "create" ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
